import React, { useState, useMemo } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { FunnelSimple, Star, Trash, X, Rows, Funnel, Plus } from '@phosphor-icons/react';
import {
  ReportDimension,
  AssetReportView,
  AssetReportFilterOperator,
} from 'shared/types/asset-report';
import { useAssetCategories } from 'hooks/useAssets';
import { ROW_LEVEL_COLORS, COL_LEVEL_COLORS, getEntityLabel } from '../constants';

// ─── Types ───────────────────────────────────────────────────────────

interface ActiveFilter {
  id: string;
  field: string;
  label: string;
  operator: AssetReportFilterOperator;
  operatorLabel: string;
  value: any;
}

interface AssetFilterPanelProps {
  onAddDimension: (dim: ReportDimension) => void;
  savedViews: AssetReportView[];
  onLoadView: (view: AssetReportView) => void;
  onDeleteView: (id: number) => void;
  rowDimensions: ReportDimension[];
  columnDimensions: ReportDimension[];
  onRemoveDimension: (field: string) => void;
  activeFilters: ActiveFilter[];
  onAddFilter: (filter: ActiveFilter) => void;
  onRemoveFilter: (filterId: string) => void;
  onReplaceFilter: (filterId: string, filter: ActiveFilter) => void;
}

// ─── Constants ───────────────────────────────────────────────────────

const QUICK_GROUPINGS: { field: string; label: string; dateGrouping?: any }[] = [
  { field: 'assetCategory.name', label: 'Categorie' },
  { field: 'assetType.name', label: 'Type' },
  { field: 'status', label: 'Status' },
  { field: 'building.googlePlaceLongAddress', label: 'Gebouw' },
  { field: 'building.location.city.name', label: 'Stad' },
  { field: 'building.company.name', label: 'Vennootschap' },
  { field: 'installer.name', label: 'Installateur' },
  { field: 'installationDate', label: 'Installatiedatum - Jaar', dateGrouping: 'year' },
  { field: 'installationDate', label: 'Installatiedatum - Kwartaal', dateGrouping: 'quarter' },
  { field: 'installationDate', label: 'Installatiedatum - Maand', dateGrouping: 'month' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Actief' },
  { value: 'inactive', label: 'Inactief' },
  { value: 'maintenance', label: 'Onderhoud' },
  { value: 'pending_installation', label: 'Installatie gepland' },
  { value: 'decommissioned', label: 'Buiten gebruik' },
];

// ─── Component ───────────────────────────────────────────────────────

const AssetFilterPanel: React.FC<AssetFilterPanelProps> = ({
  onAddDimension,
  savedViews,
  onLoadView,
  onDeleteView,
  rowDimensions,
  columnDimensions,
  onRemoveDimension,
  activeFilters,
  onAddFilter,
  onRemoveFilter,
  onReplaceFilter,
}) => {
  const { categories } = useAssetCategories();
  const [customOpen, setCustomOpen] = useState(false);
  const [customField, setCustomField] = useState('');
  const [customOp, setCustomOp] = useState('contains');
  const [customValue, setCustomValue] = useState('');

  // Get all types across all categories
  const allTypes = useMemo(() => {
    const types: { id: number; name: string; categoryName: string }[] = [];
    for (const cat of categories) {
      if (cat.assetTypes) {
        for (const t of cat.assetTypes) {
          types.push({ id: t.id, name: t.name, categoryName: cat.name });
        }
      }
    }
    return types.sort((a, b) => a.name.localeCompare(b.name));
  }, [categories]);

  // Current category/type filter values
  const categoryFilter = activeFilters.find(f => f.field === 'assetCategoryId');
  const selectedCategoryIds: number[] = categoryFilter?.value || [];

  const typeFilter = activeFilters.find(f => f.field === 'assetTypeId');
  const selectedTypeIds: number[] = typeFilter?.value || [];

  const statusFilter = activeFilters.find(f => f.field === 'status');
  const selectedStatuses: string[] = statusFilter?.value || [];

  // Toggle a category in multi-select
  const toggleCategory = (catId: number) => {
    const newIds = selectedCategoryIds.includes(catId)
      ? selectedCategoryIds.filter(id => id !== catId)
      : [...selectedCategoryIds, catId];

    if (newIds.length === 0) {
      if (categoryFilter) onRemoveFilter(categoryFilter.id);
      return;
    }

    const names = categories.filter(c => newIds.includes(c.id)).map(c => c.name);
    const label = names.length <= 2 ? names.join(', ') : `${names.length} categorieën`;
    const filter: ActiveFilter = {
      id: 'category-filter',
      field: 'assetCategoryId',
      label: `Categorie: ${label}`,
      operator: AssetReportFilterOperator.IN,
      operatorLabel: 'in',
      value: newIds,
    };

    if (categoryFilter) onReplaceFilter(categoryFilter.id, filter);
    else onAddFilter(filter);
  };

  // Toggle a type in multi-select
  const toggleType = (typeId: number) => {
    const newIds = selectedTypeIds.includes(typeId)
      ? selectedTypeIds.filter(id => id !== typeId)
      : [...selectedTypeIds, typeId];

    if (newIds.length === 0) {
      if (typeFilter) onRemoveFilter(typeFilter.id);
      return;
    }

    const names = allTypes.filter(t => newIds.includes(t.id)).map(t => t.name);
    const label = names.length <= 2 ? names.join(', ') : `${names.length} types`;
    const filter: ActiveFilter = {
      id: 'type-filter',
      field: 'assetTypeId',
      label: `Type: ${label}`,
      operator: AssetReportFilterOperator.IN,
      operatorLabel: 'in',
      value: newIds,
    };

    if (typeFilter) onReplaceFilter(typeFilter.id, filter);
    else onAddFilter(filter);
  };

  // Toggle status
  const toggleStatus = (status: string) => {
    const newStatuses = selectedStatuses.includes(status)
      ? selectedStatuses.filter(s => s !== status)
      : [...selectedStatuses, status];

    if (newStatuses.length === 0) {
      if (statusFilter) onRemoveFilter(statusFilter.id);
      return;
    }

    const names = STATUS_OPTIONS.filter(s => newStatuses.includes(s.value)).map(s => s.label);
    const label = names.length <= 2 ? names.join(', ') : `${names.length} statussen`;
    const filter: ActiveFilter = {
      id: 'status-filter',
      field: 'status',
      label: `Status: ${label}`,
      operator: AssetReportFilterOperator.IN,
      operatorLabel: 'in',
      value: newStatuses,
    };

    if (statusFilter) onReplaceFilter(statusFilter.id, filter);
    else onAddFilter(filter);
  };

  // Add custom text filter
  const handleAddCustom = () => {
    if (!customField || !customValue) return;
    const FIELDS: Record<string, string> = {
      'name': 'Naam',
      'building.googlePlaceLongAddress': 'Gebouw > Adres',
      'building.location.city.name': 'Stad',
      'building.company.name': 'Vennootschap',
      'installer.name': 'Installateur',
    };
    const OPS: Record<string, { op: AssetReportFilterOperator; label: string }> = {
      'contains': { op: AssetReportFilterOperator.CONTAINS, label: 'bevat' },
      'equals': { op: AssetReportFilterOperator.EQUALS, label: 'is' },
      'not_equals': { op: AssetReportFilterOperator.NOT_EQUALS, label: 'is niet' },
    };
    const opDef = OPS[customOp] || OPS['contains'];
    onAddFilter({
      id: `custom-${Date.now()}`,
      field: customField,
      label: `${FIELDS[customField] || customField} ${opDef.label} "${customValue}"`,
      operator: opDef.op,
      operatorLabel: opDef.label,
      value: customValue,
    });
    setCustomField('');
    setCustomValue('');
    setCustomOpen(false);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8">
            <FunnelSimple className="h-4 w-4 mr-1.5" />
            Filter & Groeperen
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[680px] p-0" align="start" sideOffset={4}>
          <div className="flex divide-x">
            {/* Filters — multi-select for category, type, status */}
            <div className="w-[260px] p-3 max-h-[400px] overflow-y-auto">
              <div className="flex items-center gap-1.5 mb-3">
                <Funnel className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-semibold">Filters</span>
              </div>

              {/* Category multi-select */}
              <div className="mb-3">
                <p className="text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">Categorie</p>
                <div className="space-y-1">
                  {categories.filter(c => c.active !== false).map(cat => (
                    <label key={cat.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-accent rounded px-1.5 py-1">
                      <Checkbox
                        checked={selectedCategoryIds.includes(cat.id)}
                        onCheckedChange={() => toggleCategory(cat.id)}
                      />
                      <span>{cat.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Type multi-select */}
              <div className="mb-3">
                <p className="text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">Type</p>
                <div className="space-y-1 max-h-[150px] overflow-y-auto">
                  {allTypes.map(t => (
                    <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-accent rounded px-1.5 py-1">
                      <Checkbox
                        checked={selectedTypeIds.includes(t.id)}
                        onCheckedChange={() => toggleType(t.id)}
                      />
                      <span>{t.name}</span>
                      <span className="text-[10px] text-muted-foreground ml-auto">{t.categoryName}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Status multi-select */}
              <div className="mb-3">
                <p className="text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">Status</p>
                <div className="space-y-1">
                  {STATUS_OPTIONS.map(s => (
                    <label key={s.value} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-accent rounded px-1.5 py-1">
                      <Checkbox
                        checked={selectedStatuses.includes(s.value)}
                        onCheckedChange={() => toggleStatus(s.value)}
                      />
                      <span>{s.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Custom text filter */}
              {!customOpen ? (
                <button
                  onClick={() => setCustomOpen(true)}
                  className="w-full text-left text-sm px-2 py-1.5 rounded-md text-primary hover:bg-primary/5 flex items-center gap-1"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Aangepast filter
                </button>
              ) : (
                <div className="space-y-2 border-t pt-2">
                  <select
                    value={customField}
                    onChange={(e) => setCustomField(e.target.value)}
                    className="w-full h-7 text-xs rounded-md border border-input bg-background px-2"
                  >
                    <option value="">Veld...</option>
                    <option value="name">Voorziening &gt; Naam</option>
                    <option value="building.googlePlaceLongAddress">Gebouw &gt; Adres</option>
                    <option value="building.location.city.name">Gebouw &gt; Stad</option>
                    <option value="building.company.name">Gebouw &gt; Vennootschap</option>
                    <option value="installer.name">Onderhoud &gt; Installateur</option>
                  </select>
                  <select
                    value={customOp}
                    onChange={(e) => setCustomOp(e.target.value)}
                    className="w-full h-7 text-xs rounded-md border border-input bg-background px-2"
                  >
                    <option value="contains">bevat</option>
                    <option value="equals">is gelijk aan</option>
                    <option value="not_equals">is niet gelijk aan</option>
                  </select>
                  <Input value={customValue} onChange={(e) => setCustomValue(e.target.value)} placeholder="Waarde..." className="h-7 text-xs" />
                  <div className="flex gap-1">
                    <Button size="sm" className="h-6 text-xs px-2" onClick={handleAddCustom}>Toevoegen</Button>
                    <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={() => setCustomOpen(false)}>Annuleren</Button>
                  </div>
                </div>
              )}
            </div>

            {/* Groeperen op */}
            <div className="flex-1 p-3">
              <div className="flex items-center gap-1.5 mb-2">
                <Rows className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-semibold">Groeperen op</span>
              </div>
              <div className="space-y-0.5">
                {QUICK_GROUPINGS.map((qg) => {
                  const allDims = [...rowDimensions, ...columnDimensions];
                  const isActive = allDims.some(d => d.field === qg.field && d.dateGrouping === qg.dateGrouping);
                  return (
                    <button
                      key={`${qg.field}-${qg.dateGrouping || ''}`}
                      onClick={() => !isActive && onAddDimension({ field: qg.field, label: qg.label, ...(qg.dateGrouping ? { dateGrouping: qg.dateGrouping } : {}) })}
                      className={`w-full text-left text-sm px-2 py-1.5 rounded-md transition-colors ${isActive ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-accent text-foreground'}`}
                    >
                      {qg.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Favorieten */}
            <div className="w-[160px] p-3">
              <div className="flex items-center gap-1.5 mb-2">
                <Star className="h-4 w-4 text-muted-foreground" weight="fill" />
                <span className="text-sm font-semibold">Favorieten</span>
              </div>
              {savedViews.length === 0 ? (
                <p className="text-xs text-muted-foreground">Geen opgeslagen weergaven</p>
              ) : (
                <div className="space-y-0.5">
                  {savedViews.map(view => (
                    <div key={view.id} className="flex items-center justify-between group text-xs px-2 py-1.5 rounded-md hover:bg-accent cursor-pointer" onClick={() => onLoadView(view)}>
                      <span className="truncate">{view.name}</span>
                      <button onClick={(e) => { e.stopPropagation(); onDeleteView(view.id); }} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive">
                        <Trash className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Active filter chips */}
      {activeFilters.map(f => (
        <span key={f.id} className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md border bg-violet-50 text-violet-700 border-violet-200">
          <Funnel className="h-3 w-3" />
          {f.label}
          <button onClick={() => onRemoveFilter(f.id)} className="ml-0.5 rounded-full hover:opacity-70 p-0.5">
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}

      {/* Row dimension chips */}
      {rowDimensions.map((dim, i) => {
        const color = ROW_LEVEL_COLORS[i] || ROW_LEVEL_COLORS[ROW_LEVEL_COLORS.length - 1];
        return (
          <span key={`row-${dim.field}-${dim.dateGrouping || ''}`} className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md border ${color.bg} ${color.text} ${color.border}`}>
            <span className="font-bold">{color.label}</span>
            {getEntityLabel(dim.field, dim.label)}
            <button onClick={() => onRemoveDimension(dim.field)} className="ml-0.5 rounded-full hover:opacity-70 p-0.5"><X className="h-3 w-3" /></button>
          </span>
        );
      })}

      {/* Column dimension chips */}
      {columnDimensions.map((dim, i) => {
        const color = COL_LEVEL_COLORS[i] || COL_LEVEL_COLORS[COL_LEVEL_COLORS.length - 1];
        return (
          <span key={`col-${dim.field}-${dim.dateGrouping || ''}`} className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md border ${color.bg} ${color.text} ${color.border}`}>
            <span className="font-bold">{color.label}</span>
            {getEntityLabel(dim.field, dim.label)}
            <button onClick={() => onRemoveDimension(dim.field)} className="ml-0.5 rounded-full hover:opacity-70 p-0.5"><X className="h-3 w-3" /></button>
          </span>
        );
      })}
    </div>
  );
};

export default AssetFilterPanel;
